import { DateTime, Duration } from 'luxon';

export function formatFlightTime(unixTime: number | undefined, timezoneFormat: string, timezone = ''): string {
    if (unixTime == null) {
        return '';
    }

    switch (timezoneFormat) {
        case 'browser':
            return formatTimeByBrowserLocale(unixTime);
            break;

        case 'airport':
            return formatTimeByTimezone(unixTime, timezone);
            break;

        case 'utc':
        default:
            return formatTimeByUtc(unixTime);
            break;
    }
}

export function formatTimeByBrowserLocale(unixTime: number): string {
    return DateTime.fromSeconds(unixTime, { zone: 'system' }).toFormat('HH:mm:ss');
}

export function formatTimeByTimezone(unixTime: number, airportTimezone: string): string {
    if (!DateTime.local().setZone(airportTimezone).isValid) {
        // If timezone is not valid, fall back to UTC
        return formatTimeByUtc(unixTime);
    }
    return DateTime.fromSeconds(unixTime, { zone: airportTimezone }).toFormat('HH:mm:ss (Z)');
}

export function formatTimeByUtc(unixTime: number): string {
    return DateTime.fromSeconds(unixTime, { zone: 'utc' }).toISOTime({ suppressMilliseconds: true }) ?? '';
}

export function parseFlightTime(isoDateTime: string): number | undefined {
    const parsedDateTime = DateTime.fromISO(isoDateTime, { zone: 'utc' });
    return parsedDateTime.isValid ? parsedDateTime.toUnixInteger() : undefined;
}

export function formatTimeDifference(seconds: number | undefined): string {
    if (seconds == null) {
        return '';
    }

    let prefix = '';
    if (seconds < 0) {
        // To clarify, the below dash is a minus (U+2212)
        // and not a standard hyphen, which may be hard to
        // differentiate depending on your font settings.
        prefix = '−';
    } else if (seconds > 0) {
        prefix = '+';
    }

    return prefix + ' ' + Duration.fromObject({ seconds: Math.abs(seconds) }).toFormat('mm:ss');
}

export function formatClockTime(clockTime: Date): string {
    return DateTime.fromJSDate(clockTime, { zone: 'utc' }).toFormat('HH:mm') + 'Z';
}

export function formatClockDate(clockTime: Date): string {
    return DateTime.fromJSDate(clockTime, { zone: 'utc' }).toFormat('dd-MMM-yyyy');
}

export function formatRelativeTime(time: Date): string {
    return DateTime.fromJSDate(time).toRelative() || '';
}

export function formatAirport(icao: string, iata: string) {
    return `${iata} / ${icao}`;
}

export function convertCamelCaseToSkewerCase(input: string): string {
    return input.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

export function parseCommaSeparatedFilterValue(filterValue: string | null | undefined): string[] {
    return (filterValue || '')
        .split(',')
        .map((value) => value.trim().toUpperCase())
        .filter((value) => value !== '');
}

export function capitalizeString(input: string): string {
    return input.charAt(0).toUpperCase() + input.slice(1);
}

export function formatFilter(filter: string[] | undefined): string {
    if (filter === undefined || filter.length == 0) {
        return 'any';
    }

    return filter.join(', ');
}

export function getDashboardRelativeUrl(dashboardId: string): string {
    return `/dashboards/${dashboardId}`;
}
